import { createContext, useState, useEffect } from "react";
import axios from "axios";
import { useCallback } from 'react';
import toast from "react-hot-toast";
import { signUpURL, LoginURL, resetPasswordURL, OTPsetURL, verifyOtpURL, googleLoginURL, getUserURL, getAllUserURL } from "../../api/api";

export const AuthContext = createContext();

const AuthContextProvider = ({ children }) => {
  
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [allUsers, setAllUsers] = useState([]);

  // Check for token in localStorage on initial load
  useEffect(() => {
    const savedToken = localStorage.getItem('token');
    if (savedToken) {
      setToken(savedToken);
    }
  }, []); // Empty dependency array ensures this only runs once after mount

  const [user, setUser] = useState(null); 

  const fetchAllUsers = useCallback(async () => {
    try {
      const res = await axios.get(getAllUserURL);
      if (res.data?.users) {
        setAllUsers(res.data.users);
        return res.data.users;
      } else {
        console.warn("Users list not found in response.");
      }
    } catch (error) {
      console.error("Error fetching all users:", error);
      toast.error("Unable to fetch users.");
    }
  }, []);
  
 const fetchUser = async (retries = 3) => {
    const token = localStorage.getItem("token");
    if (!token) {
        setUser(null);
        return;
    }

    for (let attempt = 1; attempt <= retries; attempt++) {
        try {
            setLoading(true);
            const response = await axios.get(getUserURL, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setUser(JSON.parse(response.data.data)); // Assuming data is the key containing user data
            return; // Exit function on success

        } catch (error) {
            if (axios.isCancel(error)) {
               // console.warn(`Request was aborted. Retry attempt ${attempt}/${retries}...`);
            } else if (error.code === "ECONNABORTED" || error.message.includes("Network Error")) {
                //console.warn(`Network error! Retry attempt ${attempt}/${retries}...`);
            } else {
                console.error("Failed to fetch user:", error);
                setUser(null);
                return; // Exit on non-network-related errors
            }

            if (attempt < retries) {
                await new Promise((resolve) => setTimeout(resolve, 2000)); // Wait 2 seconds before retrying
                continue; // Retry the request
            }

            //toast.error("Network issue! Please check your internet and try again.");
            setUser(null);
        } finally {
            setLoading(false);
        }
    }
};

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setUser(null);
    }
  }, [token]);


  const SignUp = async (data) => {
    try {
      setLoading(true);
      const res = await axios.post(signUpURL, data);
  
      const authToken = res.data.token;
      const authMessage = res.data.message;
      const userData = res.data.userData;
      const address = res.data.address;
      const orders = res.data.orders;
      const promoCodes = res.data.promoCodes;
      const generalNotif = res.data.generalNotif;
      const userNotif = res.data.userNotif;
      const wishlist = res.data.wishlist;
  
      if (authToken) {
        setToken(authToken);
        localStorage.setItem('token', authToken);
        localStorage.setItem('message', authMessage);
        localStorage.setItem('userData', JSON.stringify(userData));
        // localStorage.setItem('address', JSON.stringify(address));
        // localStorage.setItem('orders', JSON.stringify(orders));
        // localStorage.setItem('promoCodes', JSON.stringify(promoCodes));
        // localStorage.setItem('generalNotif', JSON.stringify(generalNotif));
        // localStorage.setItem('userNotif', JSON.stringify(userNotif));
        // localStorage.setItem('wishlist', JSON.stringify(wishlist));
      }
  
      toast.success("Signup Successful");
    } catch (error) {
      console.error(error);
  
      if (error.response && error.response.status === 400 && error.response.data?.message === "User already exists") {
        toast.error("User already exists. Try logging in.");
      } else {
        toast.error("An error occurred during sign-up");
      }
  
    } finally {
      setLoading(false);
    }
  };  

  const loginHandler = async (value) => {
    const data = {
        email: value.email,
        password: value.password,
    };

    setLoading(true);

    try {
        const res = await axios.post(LoginURL, data);
        const authToken = res.data.token;
        const authMessage = res.data.message;
        //const userData = res.data.userData;
        const address = res.data.address;
        const orders = res.data.orders;
        //const promoCodes = res.data.promoCodes;
        const generalNotif = res.data.generalNotif;
        const userNotif = res.data.userNotif;
        const wishlist = res.data.wishlist;

        if (authToken) {
            // Save token and other details to localStorage
            setToken(authToken);
            localStorage.setItem("token", authToken);
            localStorage.setItem("message", authMessage);
            // localStorage.setItem("address", JSON.stringify(address));
            // localStorage.setItem("orders", JSON.stringify(orders));
            // localStorage.setItem("generalNotif", JSON.stringify(generalNotif));
            // localStorage.setItem("userNotif", JSON.stringify(userNotif));
            // localStorage.setItem("wishlist", JSON.stringify(wishlist));

            // Display success toast
            //toast.success(authMessage);

            // Return success
            return { success: true, message: authMessage };
        }

        // If no token is received, return failure
        return { success: false, message: "Login failed" };
    } catch (err) {
        console.error("Login error response:", err.response);

        // Display error toast based on the response
        if (!err.response?.data?.errors) {
            toast.error(err.response?.data?.message || "An error occurred.");
        } else {
            if (err.response.data.errors.length > 1) {
                toast.error("Email or Password is required");
            } else {
                toast.error(err.response.data.errors[0].msg);
            }
        }

        // Return failure with the error message
        return { success: false, message: err.response?.data?.message || "Login failed." };
    } finally {
        setLoading(false);
    }
};

  const logoutHandler = () => {
    // Swal.fire({
    //   title: 'Are you sure?',
    //   text: 'You will be logged out!',
    //   icon: 'warning',
    //   showCancelButton: true,
    //   confirmButtonColor: '#3085d6',
    //   cancelButtonColor: '#d33',
    //   confirmButtonText: 'Yes, log me out!'
    // }).then((result) => {
    //   if (result.isConfirmed) {
        localStorage.removeItem("token");
        localStorage.removeItem("message");
        localStorage.removeItem('address');
        localStorage.removeItem('orders');
        localStorage.removeItem('promoCodes');
        localStorage.removeItem('selectedAddress');
        localStorage.removeItem('appliedPromo');
        localStorage.removeItem('Cart');
        localStorage.removeItem('userNotif');
        localStorage.removeItem('wishlist');
        localStorage.removeItem('adminOrders');
        setToken(null);
      }
    // });
  // };
  
  const onGoogleSuccess = async (credentialResponse) => {
    const { credential } = credentialResponse;
    try {
        const response = await axios.post(googleLoginURL, { token: credential }, {
            headers: {
                'Content-Type': 'application/json',
            },
        });

        const {
            token: authToken,
            message: authMessage,
            userData,
            address,
            orders,
            promoCodes,
            generalNotif,
            userNotif,
            wishlist,
        } = response.data;

        if (authToken) {
            // Save token and other user data to state and localStorage
            setToken(authToken);
            localStorage.setItem('token', authToken);
            localStorage.setItem('message', authMessage);
            // localStorage.setItem('address', JSON.stringify(address));
            // localStorage.setItem('orders', JSON.stringify(orders));
            // localStorage.setItem('promoCodes', JSON.stringify(promoCodes));
            // localStorage.setItem('generalNotif', JSON.stringify(generalNotif));
            // localStorage.setItem('userNotif', JSON.stringify(userNotif));
            // localStorage.setItem('wishlist', JSON.stringify(wishlist));

            // Return success response for the calling component
            return { success: true, message: authMessage };
        }

        // If token is not returned, send a failure response
        return { success: false, message: "Google login failed" };
    } catch (error) {
        console.error('Google login failed:', error);

        // Return failure response for the calling component
        return {
            success: false,
            message: error.response?.data?.message || "Google login failed due to a server error.",
        };
    }
};

  // OTP handling for both email and mobile
  const OTPset = async ({ email, mobile }) => {
    // console.log(email,mobile);
    try {
      // Ensure that either email or mobile is provided, but not both
      if (!email && !mobile) {
        throw new Error('Email or mobile must be provided');
      }
  
      const data = {};
      if (email) data.email = email;
      if (mobile) data.mobile = mobile;
 // console.log(data);
      const response = await axios.patch(OTPsetURL, data);
      
      if (response.status === 200) {
        return response.data; // Assuming the backend returns a message like 'OTP sent'
      } else {
        throw new Error('Failed to send OTP');
      }
    } catch (error) {
      console.error("Error in sending OTP:", error.response ? error.response.data : error.message);
      throw error; // Let the caller handle the error as needed
    }
  };
  

  const resetPassword = async ({ email, mobile, newPassword }) => {
    try {
      const data = { password: newPassword };
  
      // Add either email or mobile to the request body
      if (email) {
        data.email = email;
      } else if (mobile) {
        data.mobile = mobile;
      }
  
     // console.log('Request Data:', data);  // Log the request data
  
      const response = await axios.post(resetPasswordURL, data);
      
      // If the response is successful, return the data
      if (response.status === 200) {
        return response.data;
      } else {
        toast.error("Failed to reset password");
        console.error('Error Response:', response);  // Log the error response
        return null;
      }
    } catch (error) {
      console.error('Error in resetting password:', error);  // Log the error
      toast.error("An error occurred while resetting password");
      
      // Capture the full error details (response, message, etc.)
      if (error.response) {
        console.error('Error Response:', error.response.data);  // Log server response data
      } else if (error.request) {
        console.error('Error Request:', error.request);  // Log the request that was made
      } else {
        console.error('Error Message:', error.message);  // Log the error message
      }
      return null;
    }
  };
  
  // OTP verification for email or mobile with a purpose
  const verifyOtp = async ({ otp, email, mobile, purpose }) => {
    try {
     // console.log(otp, email, mobile, purpose);
      // Ensure that either email or mobile is provided for OTP verification
      const data = { otp, purpose };
      
      // Add either email or mobile to the request body
      if (email) {
        data.email = email;
      } else if (mobile) {
        data.mobile = mobile;
      }
  
      if (!data.email && !data.mobile) {
        throw new Error('Email or mobile must be provided for OTP verification');
      } 
      const response = await axios.post(verifyOtpURL, data);
  
      if (response.status === 200) {
        return response.data; // Return the success data from backend
      } else {
        throw new Error('Failed to verify OTP');
      }
    } catch (error) {
      console.error("Error in verifying OTP:", error.response ? error.response.data : error.message);
      toast.error(error.response ? error.response.data.message : 'Incorrect OTP. Please try again.');
      return null; // Return null to indicate failure
    }
  };
  
  
  return (
    <AuthContext.Provider
      value={{
        loginHandler,
        token,
        loading,
        logoutHandler,
        onGoogleSuccess,
        SignUp,
        resetPassword,
        verifyOtp,
        OTPset,
        fetchUser,
        user,
        allUsers,
        fetchAllUsers,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContextProvider;
